import React, { useState, useEffect } from "react";
import { fetchPrompts } from "../Utils/PromptUtils";
import { useSelector } from "react-redux";
import { selectAccessToken, selectAuthenticated } from "../selectors";
import { useNavigate } from "react-router-dom";
import SignInPrompt from "./SignInPrompt";
import { AlignCenterIcon } from "lucide-react";
import { SparkleFilled } from "./icons";

const PromptModel = ({
  setShowPromptGalleryModal,
  onSelectPrompt,
  promptType,
}) => {
  const [prompts, setPrompts] = useState([]);
  const [promptsFetched, setPromptsFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const accessToken = useSelector(selectAccessToken);
  const authenticated = useSelector(selectAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    // Only fetch prompts if user is authenticated (explicitly true)
    if (authenticated === true) {
      const getPrompts = async () => {
        const response = await fetchPrompts(accessToken, promptType);
        if (response.status === "success") {
          setPrompts(response.data);
          const uniqueCategories = [
            ...new Set(response.data.map((p) => p.category).filter(Boolean)),
          ];
          setCategories(uniqueCategories);
          setPromptsFetched(true);
        } else {
          console.log("Failed to fetch prompts", response.message);
        }
      };
      getPrompts();
    } else {
      // Reset promptsFetched when not authenticated to prevent loading state
      setPromptsFetched(false);
    }
  }, [authenticated, accessToken, promptType]);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredPrompts = prompts
    .map((prompt) => {
      const matchesCategory =
        selectedCategory === "all" || prompt.category === selectedCategory;

      if (!matchesCategory) {
        return null;
      }

      if (!normalizedQuery) {
        return prompt;
      }

      const categoryMatch = (prompt.category || "")
        .toLowerCase()
        .includes(normalizedQuery);

      if (Array.isArray(prompt.prompts)) {
        const matchingEntries = prompt.prompts.filter(
          (entry) =>
            typeof entry === "string" &&
            entry.toLowerCase().includes(normalizedQuery)
        );

        if (matchingEntries.length > 0) {
          return { ...prompt, prompts: matchingEntries };
        }

        return categoryMatch ? prompt : null;
      }

      if (
        typeof prompt.prompts === "string" &&
        prompt.prompts.toLowerCase().includes(normalizedQuery)
      ) {
        return prompt;
      }

      return categoryMatch ? prompt : null;
    })
    .filter(Boolean);

  const handlePromptSelect = (prompt) => {
    setSelectedPrompt(prompt);
    if (onSelectPrompt) {
      onSelectPrompt(prompt);
    }
    setShowPromptGalleryModal(false);
  };

  const handleCopyPrompt = (prompt) => {
    navigator.clipboard.writeText(prompt).then(() => {
      setToastMessage("The prompt is on your clipboard.");
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    }).catch(() => {});
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay p-2 sm:p-4"
      onClick={() => setShowPromptGalleryModal(false)}
    >
      {showToast && (
        <div
          className="fixed bottom-5 left-1/2 z-[60] flex w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 items-start gap-3 rounded-2xl bg-white p-4 shadow-[0_0_0_1px_var(--hairline)]"
          role="status"
        >
          <div className="min-w-0 flex-1">
            <p className="font-Mulish text-sm font-semibold text-secondary">Copied</p>
            <p className="mt-1 font-Mulish text-sm text-graphite">{toastMessage}</p>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={() => setShowToast(false)}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white font-Mulish text-sm text-primary-700 shadow-[0_0_0_1px_var(--hairline)]"
          >
            ×
          </button>
        </div>
      )}

      <div 
        className={`relative my-auto flex w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_var(--hairline)] ${authenticated === true ? 'h-auto max-h-[85vh]' : 'h-[400px]'}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={() => setShowPromptGalleryModal(false)}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-white text-secondary shadow-[0_0_0_1px_var(--hairline)] sm:right-4 sm:top-4"
          aria-label="Close modal"
          title="Close"
        >
          <svg
            className="h-5 w-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        <div className="flex-shrink-0 border-b border-hairline p-4 sm:p-5 md:p-6">
          <h2 className="font-Mulish text-lg font-semibold text-secondary sm:text-xl">Prompt Gallery</h2>
        </div>

        {/* Content */}
        <div className={`flex-1 min-h-0 ${authenticated === true ? 'overflow-y-auto' : 'overflow-hidden flex items-center justify-center'}`}>
        {authenticated !== true ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <SignInPrompt
              title="Sign in to see prompts"
              message="Saved prompts will show up here."
              onSignIn={(e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                setShowPromptGalleryModal(false);
                setTimeout(() => {
                  navigate("/signin");
                }, 100);
              }}
              className="px-6 py-8"
            />
          </div>
        ) : (
        <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden min-h-0">
          {/* Left */}
          <div className="w-full overflow-visible border-hairline bg-white p-4 md:w-60 md:overflow-y-auto md:border-b-0 md:border-r">
            <div className="group relative mb-4">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <svg
                  className="h-4 w-4 text-graphite group-focus-within:text-primary-700"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border-0 bg-white py-2.5 pl-10 pr-3 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] placeholder:text-graphite selection:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-200"
              />
            </div>
            <div className="block md:hidden">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full rounded-lg border-0 bg-white py-2.5 pl-3 pr-8 font-Mulish text-sm text-secondary shadow-[0_0_0_1px_var(--hairline)] focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                <option value="all">All</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <ul className="hidden space-y-2 md:block">
              <li>
                <button
                  onClick={() => setSelectedCategory("all")}
                  className={`w-full rounded-lg bg-white px-3 py-2 text-left font-Mulish text-sm ${
                    selectedCategory === "all"
                      ? "border-2 border-primary-500 font-semibold text-secondary"
                      : "border border-hairline text-graphite hover:border-primary-200"
                  }`}
                >
                  All
                </button>
              </li>
              {categories.map((category) => (
                <li key={category}>
                  <button
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full rounded-lg bg-white px-3 py-2 text-left font-Mulish text-sm ${
                      selectedCategory === category
                        ? "border-2 border-primary-500 font-semibold text-secondary"
                        : "border border-hairline text-graphite hover:border-primary-200"
                    }`}
                  >
                    {category}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Right */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0 ">
            {promptsFetched ? (
              filteredPrompts.length > 0 ? (
                <div
                  className={`grid ${
                    selectedCategory === "all"
                      ? "grid-cols-1 md:grid-cols-2"
                      : "grid-cols-1"
                  } gap-4 w-full`}
                >
                  {filteredPrompts.map((prompt) => (
                    <div key={prompt.id} className="space-y-0">
                      <div className="space-y-2">
                        {Array.isArray(prompt.prompts) ? (
                          prompt.prompts.map((singlePrompt, index) => (
                            <div
                              key={index}
                              className="rounded-lg bg-white p-3 shadow-[0_0_0_1px_var(--hairline)] transition-colors hover:bg-hover-tint"
                            >
                              <div className="flex items-start ">
                                <div className="flex-1 min-w-0">
                                  <div className="flex gap-3">
                                    <p className="mt-1 flex items-start text-xs text-graphite">
                                      <AlignCenterIcon className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                                    </p>
                                    <p className="line-clamp-3 font-Mulish text-sm text-graphite">
                                      {singlePrompt}
                                    </p>
                                  </div>
                                  <div className="mt-2 flex items-center justify-between">
                                    <span className="mr-3 font-Mulish text-xs text-graphite">
                                      {prompt.category || ""}
                                    </span>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() =>
                                          handlePromptSelect(singlePrompt)
                                        }
                                        className="rounded px-2 py-1 font-Mulish text-xs font-semibold text-primary-700 hover:bg-primary-50"
                                      >
                                        Use
                                      </button>
                                      <button
                                        onClick={() =>
                                          handleCopyPrompt(singlePrompt)
                                        }
                                        className="rounded px-2 py-1 font-Mulish text-xs text-graphite hover:bg-hover-tint hover:text-secondary"
                                      >
                                        Copy
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="rounded-lg bg-white p-3 shadow-[0_0_0_1px_var(--hairline)] transition-colors hover:bg-hover-tint">
                            <div className="flex items-start gap-3">
                              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-white shadow-[0_0_0_1px_var(--hairline)]">
                                <SparkleFilled className="h-6 w-6 text-primary-700" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="line-clamp-1 font-Mulish text-sm font-semibold leading-5 text-secondary">
                                  Prompt
                                </div>
                                <div className="line-clamp-1 font-Mulish text-sm text-graphite">
                                  {prompt.prompts}
                                </div>
                                <div className="mt-2 flex items-center justify-between">
                                  <span className="mr-3 font-Mulish text-xs text-graphite">
                                    {prompt.category || ""}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <button
                                      onClick={() =>
                                        handlePromptSelect(prompt.prompts)
                                      }
                                      className="rounded px-2 py-1 font-Mulish text-xs font-semibold text-primary-700 hover:bg-primary-50"
                                    >
                                      Use
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleCopyPrompt(prompt.prompts)
                                      }
                                      className="rounded px-2 py-1 font-Mulish text-xs text-graphite hover:bg-hover-tint hover:text-secondary"
                                    >
                                      Copy
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex h-full min-h-[400px] flex-col items-center justify-center px-4 text-center">
                  <div className="mb-3 text-primary-700">
                    <svg
                      className="h-8 w-8"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="font-Mulish text-lg font-semibold text-secondary">
                    No prompts found
                  </p>
                  <p className="mt-1 font-Mulish text-sm text-graphite">
                    Try a different search or category.
                  </p>
                </div>
              )
            ) : (
              <div className="flex h-full min-h-[400px] items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-700"></div>
              </div>
            )}
          </div>
        </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default PromptModel;
